import Pagination from "@components/Pagination/Pagination";
import usePermissions from "@utils/hooks/usePermissions";
import { useStoredMinerTotal } from "@utils/hooks/useStoredMiners";
import useMediaQuery from "@utils/hooks/utils";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import styles from "../Machines.module.scss";
import DraggedRows from "../MinerRow/DraggedRows";
import { MinerTools } from "../MinerTools/MinerTools";
import minerTableHeaders, {
  MinersTableHeaderId,
} from "./Header/minerTableHeaders";
import MinersTableDesktopView, {
  type TableItemStyles,
} from "./MinersTableDesktopView";
import MinersTableMobileView from "./MinersTableMobileView";
import { IMinersTableProps } from "./MinersTable.props";
import { TableHeadItem } from "./tableHead";
import {
  MinerSortFields,
  useMinersTableControl,
} from "./useMinersTableControl";

const CHECKBOX_WIDTH: number = 50;
const CHECKBOX_HEIGHT: number = 100;

// const MINER_SEARCH_STORAGE_KEY = "WATTMANOR:search_storage";
// const MINER_SEARCH_UNKNOWN_STORAGE_KEY = "WATTMANOR:search_unknown_storage";

export const rowWidth = (head: TableHeadItem[], isAdmin?: boolean) =>
  head
    .filter((h) => h.sort !== MinerSortFields.CUSTOMER || isAdmin)
    .map((item) => item.width)
    .reduce((a, x) => a + x, 0) + CHECKBOX_WIDTH;

export const rowHeight = (head: TableHeadItem[], isAdmin?: boolean) =>
  head
    .filter((h) => h.sort !== MinerSortFields.CUSTOMER || isAdmin)
    .map((item) => item.height)
    .reduce((a, x) => a + x, 0) + CHECKBOX_HEIGHT;

export const calcColumnWidth = (column: TableHeadItem, rwd: number) => {
  if (column.sort === MinerSortFields.ID) return 15;
  return Math.round((column.width * 100) / rwd);
};
export const calcColumnHeight = (column: TableHeadItem, rhg: number) => {
  if (column.sort === MinerSortFields.ID) return 10;
  return Math.round((column.height * 100) / rhg);
};
export const checkboxWidth = (rwd: number) =>
  Math.round((CHECKBOX_WIDTH * 100) / rwd);
export const checkboxHeight = (rhg: number) =>
  Math.round((CHECKBOX_HEIGHT * 100) / rhg);

export const colStyle = (
  column: TableHeadItem,
  vert: boolean,
  rwd: number,
  rhg: number
) => ({
  [vert ? "height" : "width"]: vert
    ? `${calcColumnHeight(column, rhg)}%`
    : `${calcColumnWidth(column, rwd)}%`,
  ...(vert ? { overflow: "hidden" } : {}),
});

export const chbStyle = (
  vert: boolean,
  rwd: number,
  rhg: number
): React.CSSProperties => ({
  [vert ? "height" : "width"]: vert
    ? `${checkboxHeight(rhg)}%`
    : `${checkboxWidth(rwd)}%`,
});

function MinersTable(props: IMinersTableProps) {

  const isSm = useMediaQuery("max", 768); // see variables.scss / $viewport--sm
  const control = useMinersTableControl(props);
  const isTablet = useMediaQuery("max", 1025); // see variables.scss / $viewport--md
  // const isDesktop = useMediaQuery("min", 1025); // see variables.scss / $viewport--md
  const tableElement = useRef<HTMLDivElement>(null);
  const [tableStyles, setTableStyles] = useState<TableItemStyles>();
  //const cStyleRef = useRef<{ [x: string]: string }>();
  const total = useStoredMinerTotal();
  const { canMinerPorts } = usePermissions();

  useEffect(() => {
    if (tableElement.current && props.wrapperDivRef) {
      props.wrapperDivRef.current = tableElement.current;
    }
  }, [tableElement.current]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey) {
        if (!window.matchMedia(`(max-width:1025px)`).matches) {
          const element = document.getElementById("table_wrapper_id");
          if (element) {
            if (!element.classList.contains(styles.noSelect)) {
              element.classList.add(styles.noSelect);
            }
          }
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      // /*shiftPressed &&*/ !isTablet
      if (!window.matchMedia(`(max-width:1025px)`).matches) {
        // dev.log(`Keyup Class: ${styles.noSelect}`, isTables);
        const element = document.getElementById("table_wrapper_id");
        if (element && element.classList.contains(styles.noSelect)) {
          element.classList.remove(styles.noSelect);
        }
      }
    };

    /* eslint-disable-next-line no-restricted-globals */
    addEventListener("keydown", onKeyDown);
    /* eslint-disable-next-line no-restricted-globals */
    addEventListener("keyup", onKeyUp);

    return () => {
      /* eslint-disable-next-line no-restricted-globals */
      removeEventListener("keydown", onKeyDown);
      /* eslint-disable-next-line no-restricted-globals */
      removeEventListener("keyup", onKeyUp);
    };
  }, []);

  useEffect(() => {
    const rwd = rowWidth(minerTableHeaders, control.isAdmin);
    const rhg = rowHeight(minerTableHeaders, control.isAdmin);
    const wdStyle = (column: TableHeadItem) =>
      colStyle(column, isTablet, rwd, rhg);
    const checkboxStyle: React.CSSProperties = chbStyle(isTablet, rwd, rhg);
    const rowsStyles: any[] = [];
    minerTableHeaders
      .filter(
        (item) => control.isAdmin || item.id !== MinersTableHeaderId.CUSTOMER
      )
      .map((header) => {
        rowsStyles.push(wdStyle(header));
      });
    setTableStyles({
      checkboxStyle,
      rowsStyles,
    });
  }, [control.isAdmin]);

  return (
    <div className={styles.data} ref={tableElement}>
      <DraggedRows />
      <MinerTools
        onSearch={control.setSearch}
        tab={control.tab}
        showConnected={control.connected && !(control.refreshLoad || control.loading)}
      />
      <div
        id="table_wrapper_id"
        className={cn({
          [styles.table_wrapper]: !isTablet,
          [styles.noSelect]: /*shiftPressed &&*/ !isTablet,
          [styles.table_unknown]: control.tab === "unknown",
        })}
        ref={control.tableWrapperRef}
      >
        <div className={styles.upper_pagination} ref={props.upperPaginationRef}>
          <Pagination
            refresh={props.refresh}
            page={control.page}
            pageSize={control.pageSize}
            sort={control.sort}
            total={control.total}
            getPage={control.setPage}
          />
        </div>
        {isTablet ? (
          <MinersTableMobileView control={control} isSm={isSm} />
        ) : (
          <MinersTableDesktopView
            canMinerPorts={canMinerPorts}
            control={control}
            isMinerStatusesModal={props.isMinerStatusesModal}
            tableStyles={tableStyles}
            total={total}
          />
        )}
        <Pagination
          refresh={props.refresh}
          page={control.page}
          pageSize={control.pageSize}
          sort={control.sort}
          total={control.total}
          getPage={control.setPage}
          className={styles.pages}
          showPageSize={false}
        />
      </div>
    </div>
  );
}

export default MinersTable;